import { Color, Text } from '@billy/design-system'
import { colors } from '@shinetools/lumen-tokens/web/colors'
import isNil from 'lodash/isNil'
import React, { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'

import { BankAccountSelect } from '@features/bankAccounts/elements/BankAccountSelect'
import { AccountNature } from '@modules-deprecated/app/accounts/types'
import { useUserOrganization, useUserOrganizationSettings } from '@modules-deprecated/app/organization'
import { fetchAccountBalance } from '@modules-deprecated/bankReconciliation/query-api'

import { QueryKeys } from '../../../../../../enums/queryKeys'
import { Scope } from '../../../../../../enums/scope'
import { isAuthorized } from '../../../../../../utils/isAuthorized'
import { useMissingReceiptsBankLines } from '../../../../../thingsToDo/hooks/useMissingReceiptsBankLines'
import { useBankWidget } from '../../../../contexts/bankWidgetContext'
import { Widget } from '../../../../elements/Widget'
import * as Styled from '../../styles'
import { getAccountData } from '../../utils/getAccountData'
import { BankConnection } from '../bankConnection/BankConnection'
import { BankDisconnection } from '../bankDisconnection/BankDisconnection'
import { ConnectionCTA } from '../connectionCTA/ConnectionCTA'

const DEFAULT_CURRENCY_ID = 'DKK'
const TODAYS_DATE = new Date()

export const StandardBankIntegration = (): ReactElement | null => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { organizationSettings } = useUserOrganizationSettings()

  const { isLoadingAccounts, selectedConnectedAccount, setSelectedBankAccountId, unConnectedBankAccounts } =
    useBankWidget()

  const isInboxEnabled = organizationSettings?.isInboxEnabled === '1'

  const { accountName, balance, connection, currency, externalAccountId, id, type } =
    getAccountData(selectedConnectedAccount) || {}

  const {
    bankLines: missingReceiptsBankLines,
    isLoading: isLoadingReceipts,
    isError: isMissingReceiptsError,
  } = useMissingReceiptsBankLines(isInboxEnabled)

  const {
    data: accountBalance,
    isLoading: isAccountBalanceLoading,
    isError: isAccountBalanceError,
  } = useQuery(
    [QueryKeys.AccountBalance, externalAccountId, currency],
    () => fetchAccountBalance(externalAccountId as string, currency || DEFAULT_CURRENCY_ID, TODAYS_DATE),
    {
      enabled: !!externalAccountId && !!organizationSettings,
    },
  )

  const isError = isAccountBalanceError || (isMissingReceiptsError && isInboxEnabled) || !!connection?.requiresUpdate

  const isLoading =
    isLoadingAccounts || isAccountBalanceLoading || isLoadingReceipts || !organization || !organizationSettings

  const isBankConnected = !!selectedConnectedAccount
  const isMissingReceipts = missingReceiptsBankLines?.length > 0
  const shouldShowBankConnection = isBankConnected || isLoading || isError
  const unConnectedBankAccountsIds = unConnectedBankAccounts?.map((account) => account.id) || []
  const backgroundColor = shouldShowBankConnection ? Color.White : colors.surface.highlight.beige.subtle.default

  const handleSelectBankAccount = useCallback(
    (id?: string) => {
      if (id) {
        setSelectedBankAccountId(id)
      }
    },
    [setSelectedBankAccountId],
  )

  const getAccountBalance = () => {
    if (isNil(accountBalance)) {
      return undefined
    }

    const accountBalanceAmount = accountBalance.balance

    if (type === AccountNature.Liability && accountBalanceAmount !== 0) {
      // AccountNature.Liability requires the balance to be converted to the opposite. So positive -> negative and vice versas
      return accountBalanceAmount < 0 ? Math.abs(accountBalanceAmount) : -Math.abs(accountBalanceAmount)
    }

    return accountBalanceAmount
  }

  if (!shouldShowBankConnection && !isAuthorized(Scope.BankSyncWrite)) {
    return null
  }

  return (
    <Widget
      backgroundColor={backgroundColor}
      title={shouldShowBankConnection ? t('dashboard.widget.bank-integration.title') : undefined}
      sideActions={
        !isLoading &&
        (!!externalAccountId || isError) && (
          <ConnectionCTA accountId={externalAccountId} bankAccountId={id} isError={isError} />
        )
      }
    >
      {shouldShowBankConnection ? (
        <>
          <BankConnection
            accountBalanceAmount={getAccountBalance()}
            accountCurrencyId={currency}
            bankBalanceAmount={balance}
            isError={isError}
            isLoading={isLoading}
          />

          {!isLoading && (
            <Styled.Footer>
              <Text weight="regular" variant="bodySM" colorVariant="secondary">
                {!isError && !isMissingReceipts ? accountName : ''}
              </Text>
              <BankAccountSelect
                dropdownSize="fitTrigger"
                onSelect={handleSelectBankAccount}
                selectedId={externalAccountId || ''}
                excludedAccounts={unConnectedBankAccountsIds}
              />
            </Styled.Footer>
          )}
        </>
      ) : (
        <BankDisconnection />
      )}
    </Widget>
  )
}
